/**
 * The browser's copy of the first-run onboarding rules (issue #289).
 *
 * THE SERVER DECIDES; THIS FILE ONLY GREYS OUT A BUTTON. `api/src/lib/
 * onboarding.ts` is the authority on whether a player is walked through the
 * first-run steps and where they resume — the client asks
 * `GET /players/me/onboarding` and obeys. What lives here is the same two
 * completeness rules evaluated against the form state the player is typing
 * into right now, so the Continue button can enable the instant they pick a
 * playing role instead of after a save/round-trip. If the two copies ever
 * disagree, the server wins and the worst case is a button that looks wrong
 * for a moment; nothing here can let a player past a step the API considers
 * unfinished, because nothing here writes the marker.
 *
 * Kept as its own module (rather than inline in the wrapper) for the same
 * reason `lib/redirectTarget.js` is: a rule that two files must agree on is
 * a rule worth naming, testing, and importing.
 */

/** Where the walkthrough lives. One route; the step is a query parameter,
 * exactly like `/development?tab=identity`. */
export const ONBOARDING_PATH = "/onboarding";

/**
 * Step 1's on-page failure copy (#1077, D118/D57) — verbatim from
 * `SetUp.dc.html` (`profile-could-not-load` and `save-failed`). Failures
 * stay on the page; none of these is ever a toast.
 */
export const PROFILE_LOAD_FAILED_TITLE = "Couldn't load your profile";
export const PROFILE_LOAD_FAILED_BODY = "Nothing has been lost — we just couldn't reach the server.";
export const PROFILE_LOAD_FAILED_RETRY = "Try again";
export const STEP_SAVE_FAILED = "We couldn't save that just now — please try again.";

export const ONBOARDING_STEPS = Object.freeze(["profile", "identity"]);

/** Total steps, as shown to the player ("Step 1 of 2"). Derived, so the
 * label cannot drift from the list above. */
export const ONBOARDING_STEP_COUNT = ONBOARDING_STEPS.length;

/** 1-based position of a step, for the "Step N of M" label. Returns 1 for
 * anything unrecognised — a bad `?step=` lands on the first step rather than
 * rendering "Step 0 of 2". */
export function stepNumber(step) {
  const i = ONBOARDING_STEPS.indexOf(step);
  return i === -1 ? 1 : i + 1;
}

/** Normalises a `?step=` value to a real step, defaulting to the first. */
export function normalizeStep(step) {
  return ONBOARDING_STEPS.includes(step) ? step : ONBOARDING_STEPS[0];
}

const filled = (v) => typeof v === "string" && v.trim() !== "";

/**
 * Step 1's bar: `playing_role` plus at least one of `batting_style` /
 * `bowling_style`. Name and date of birth were captured at registration;
 * the photo is never required (and cannot be uploaded yet — #294).
 *
 * Takes the API's snake_case player shape, which is what `/players/me`
 * returns and what `PlayerProfile.jsx` holds in state.
 *
 * MIRRORS `isProfileStepComplete` in `api/src/lib/onboarding.ts`.
 */
export function isProfileStepComplete(player) {
  if (!player || typeof player !== "object") return false;
  return filled(player.playing_role) && (filled(player.batting_style) || filled(player.bowling_style));
}

/**
 * Step 2's bar: the Player Vision section has at least one real answer in
 * it. The other three Identity & Vision sections are not part of the walk at
 * all — they stay on the Development Plan tab, to be filled in when the
 * player has something to say.
 *
 * MIRRORS `hasVisionContent` in `api/src/lib/onboarding.ts`, including the
 * array handling that makes the form's untouched `top_ambitions: ["", "", ""]`
 * read as empty.
 */
export function hasVisionContent(vision) {
  if (!vision || typeof vision !== "object" || Array.isArray(vision)) return false;
  return Object.values(vision).some((v) => {
    if (typeof v === "string") return v.trim() !== "";
    if (Array.isArray(v)) return v.some((item) => typeof item === "string" && item.trim() !== "");
    if (typeof v === "number") return true;
    if (typeof v === "boolean") return v;
    return false;
  });
}

/**
 * Step 2's save (#872, D172 S2). Set-up step 2 shows and edits exactly one
 * vision field — `short_term_goal` — but `PUT /players/me/identity`
 * replaces the WHOLE `vision` section server-side (`api/src/db/repos/
 * playerIdentity.ts`'s `upsertPlayerIdentity`), so the payload has to carry
 * every other field this screen never touches, unchanged, or the save
 * wipes them. `vision` here is whatever `OnboardingNew.jsx` last loaded
 * from `GET /players/me/identity` (its own `visionRef`) — a pure function,
 * kept here rather than inline, so "never overwrites the other vision
 * answers" is a claim this file's own test proves without mounting a
 * component (this repo's convention — see this file's own header).
 *
 * Mirrors the shape `PlayerIdentity.jsx`'s `save("vision")` already relies
 * on (load the section, edit one field of it, PUT the section back) — that
 * file's `mergeLoadedIdentity` does the same job for a form editing several
 * fields at once; this is the one-field version.
 */
export function withShortGoal(vision, value) {
  const base = vision && typeof vision === "object" && !Array.isArray(vision) ? vision : {};
  return { ...base, short_term_goal: value };
}
